'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ExternalLink } from 'lucide-react';
import { submitLeadB2C } from '@/services/leadService';
import {
  trackLeakCaptureSubmit,
  trackLeakCaptureSkip,
  type LeakRedirectType,
} from '@/lib/analytics';
import { INTERNAL_LINKS } from '@/constants/links';
import type { LandingColors } from '@/constants/landing-content/types';
import { trackDirectoryFormSubmission } from '@/lib/directory-form-analytics';

export interface LeakRedirect {
  /** URL externa final a la que se redirige tras capturar (o saltar) */
  url: string;
  /** Tipo de CTA que originó la salida */
  type: LeakRedirectType;
}

interface LeakListing {
  id: string;
  name: string;
  slug: string;
  city?: string | null;
}

interface LeadCaptureRedirectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listing: LeakListing;
  redirect: LeakRedirect | null;
  colors: LandingColors;
}

/**
 * Copy del modal adaptado al tipo de CTA externo. Interceptamos la salida del
 * funnel para capturar el email antes de mandar al usuario fuera del ecosistema.
 */
const COPY: Record<
  LeakRedirectType,
  { title: (name: string) => string; description: string; cta: string }
> = {
  website: {
    title: (name) => `Antes de visitar ${name}`,
    description:
      'Revisamos todas las ofertas y novedades de este centro. Déjanos tu correo y te avisamos cuando haya cambios o promociones.',
    cta: 'Visitar web',
  },
  whatsapp: {
    title: (name) => `Antes de escribir a ${name}`,
    description:
      'Guarda este centro y te avisamos de cambios de horarios, precios o nuevas clases.',
    cta: 'Ir a WhatsApp',
  },
  phone: {
    title: (name) => `Antes de llamar a ${name}`,
    description:
      'Guarda este centro y te avisamos de cambios de horarios, precios o nuevas clases.',
    cta: 'Llamar',
  },
  email: {
    title: (name) => `Antes de escribir a ${name}`,
    description:
      'Guarda este centro y te avisamos de cambios de horarios, precios o nuevas clases.',
    cta: 'Enviar email',
  },
  instagram: {
    title: (name) => `Antes de ir al Instagram de ${name}`,
    description:
      'Déjanos tu correo y te recomendamos centros parecidos en tu zona.',
    cta: 'Ir a Instagram',
  },
  facebook: {
    title: (name) => `Antes de ir al Facebook de ${name}`,
    description:
      'Déjanos tu correo y te recomendamos centros parecidos en tu zona.',
    cta: 'Ir a Facebook',
  },
  youtube: {
    title: (name) => `Antes de ir al YouTube de ${name}`,
    description:
      'Déjanos tu correo y te recomendamos centros parecidos en tu zona.',
    cta: 'Ir a YouTube',
  },
  maps: {
    title: (name) => `Antes de ir a ${name}`,
    description:
      'Déjanos tu correo y te avisamos de horarios, clases y novedades antes de ir.',
    cta: 'Cómo llegar',
  },
};

/** Abre la URL externa respetando el gesto nativo (tel:/mailto: navegan; el resto en pestaña nueva) */
function goExternal(url: string, type: LeakRedirectType) {
  if (typeof window === 'undefined') return;
  if (type === 'phone' || type === 'email') {
    window.location.href = url;
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export function LeadCaptureRedirectModal({
  open,
  onOpenChange,
  listing,
  redirect,
  colors,
}: LeadCaptureRedirectModalProps) {
  const [email, setEmail] = useState('');
  const [firstname, setFirstname] = useState('');
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Reset al cerrar
  useEffect(() => {
    if (!open) {
      setEmail('');
      setFirstname('');
      setConsentMarketing(false);
      setIsSubmitting(false);
      setErrorMessage('');
    }
  }, [open]);

  if (!redirect) return null;

  const copy = COPY[redirect.type];
  const isFormValid = /\S+@\S+\.\S+/.test(email) && consentMarketing;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !redirect) return;

    setIsSubmitting(true);
    setErrorMessage('');

    const result = await submitLeadB2C({
      email,
      firstname: firstname || '',
      instrument: '',
      level: '',
      city: listing.city || undefined,
      source: `directorio_${redirect.type}:${listing.slug}`,
      marketing_consent: consentMarketing,
    });

    setIsSubmitting(false);

    if (result.success) {
      trackLeakCaptureSubmit(listing.id, redirect.type);
      trackDirectoryFormSubmission('lead_capture', 'success', { listingId: listing.id });
      const { url, type } = redirect;
      onOpenChange(false);
      // Redirigir tras cerrar para no chocar con el desmontaje del modal
      setTimeout(() => goExternal(url, type), 50);
    } else {
      setErrorMessage(result.error || 'Ha ocurrido un error. Inténtalo de nuevo.');
      trackDirectoryFormSubmission('lead_capture', 'failure', { listingId: listing.id, reason: 'api_error' });
    }
  };

  const handleSkip = () => {
    if (!redirect) return;
    trackLeakCaptureSkip(listing.id, redirect.type);
    const { url, type } = redirect;
    onOpenChange(false);
    setTimeout(() => goExternal(url, type), 50);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-landing max-h-[90dvh] overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <DialogHeader>
              <DialogTitle className="text-center text-2xl font-landing font-bold tracking-tight text-gray-900">
                {copy.title(listing.name)}
              </DialogTitle>
              <DialogDescription className="text-center text-base text-gray-600">
                {copy.description}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-6">
              {/* Email (requerido) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Email</label>
                <Input
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  required
                  autoFocus
                />
              </div>

              {/* Nombre (opcional) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Nombre <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <Input
                  type="text"
                  placeholder="Tu nombre"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  className="h-11"
                />
              </div>

              {/* Consentimiento de marketing (RGPD) — opt-in explícito, requerido */}
              <div className="flex items-start gap-3 pt-1">
                <Checkbox
                  id="leak-consent"
                  checked={consentMarketing}
                  onCheckedChange={(checked) => setConsentMarketing(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="leak-consent" className="text-xs text-gray-600 cursor-pointer leading-relaxed">
                  Acepto recibir novedades de centros de música y la{' '}
                  <a href={INTERNAL_LINKS.privacidad} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline" style={{ color: colors.primary }}>
                    Política de privacidad
                  </a>{' '}
                  y los{' '}
                  <a href={INTERNAL_LINKS.terminos} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline" style={{ color: colors.primary }}>
                    Términos
                  </a>.
                </label>
              </div>

              {errorMessage && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-600 text-center bg-red-50 py-2 px-3 rounded-lg"
                >
                  {errorMessage}
                </motion.p>
              )}

              {/* CTA primario: capturar y salir */}
              <Button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="w-full h-12 text-base font-semibold rounded-xl shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{
                  backgroundColor: colors.primary,
                  boxShadow: isFormValid ? `0 8px 16px -4px ${colors.primary}40` : 'none',
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    {copy.cta}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              {/* CTA secundario: salir sin dejar email (no secuestramos la salida) */}
              <button
                type="button"
                onClick={handleSkip}
                disabled={isSubmitting}
                className="w-full text-xs text-gray-400 hover:text-gray-600 transition text-center disabled:opacity-50"
              >
                Ir directamente
              </button>
            </form>
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

export default LeadCaptureRedirectModal;
